import React, { useEffect } from 'react';
import { useVideoAd } from '../hooks/useVideoAd';
import { useViewport } from '../hooks/useViewport';

interface VideoAdModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onError?: () => void;
  adTagUrl?: string;
}

// Google IMA test VAST tag (for development)
const DEFAULT_AD_TAG_URL =
  'https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/6062/video_test&cust_params=deployment%3Ddevsite%26sample_ct%3Dlinear&ciu_szs=300x250&impl=s&gdfp_req=1&env=vp&output=vast&unviewed_position_start=1&correlator=';

export default function VideoAdModal({
  isOpen,
  onComplete,
  onError,
  adTagUrl = DEFAULT_AD_TAG_URL,
}: VideoAdModalProps) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobile = viewport.isMobile;
  const {
    adDisplayContainerRef,
    videoRef,
    isLoading,
    isPlaying,
    error,
    loadAd,
    cleanup,
  } = useVideoAd({
    adTagUrl,
    onAdLoaded: () => {
      console.log('[VideoAdModal] Ad loaded');
    },
    onAdStarted: () => {
      console.log('[VideoAdModal] Ad started playing');
    },
    onAdComplete: () => {
      console.log('[VideoAdModal] Ad completed');
      onComplete();
    },
    onAdError: (err) => {
      console.error('[VideoAdModal] Ad error:', err);
      // On error, proceed to game (don't block user)
      if (onError) {
        onError();
      } else {
        onComplete();
      }
    },
    onAdSkipped: () => {
      console.log('[VideoAdModal] Ad skipped');
      onComplete();
    },
    timeout: 10000, // 10 second timeout
  });

  useEffect(() => {
    if (isOpen) {
      // Mark that ad is showing (for HUD timer pause)
      if (typeof window !== 'undefined') {
        (window as any).__VIDEO_AD_SHOWING__ = true;
      }

      // Small delay to ensure container is rendered
      const timer = setTimeout(() => {
        loadAd();
      }, 200);

      return () => {
        clearTimeout(timer);
        // Clear the flag when ad closes
        if (typeof window !== 'undefined') {
          (window as any).__VIDEO_AD_SHOWING__ = false;
        }
      };
    } else {
      // Only cleanup when modal closes
      cleanup();
      // Clear the flag when ad closes
      if (typeof window !== 'undefined') {
        (window as any).__VIDEO_AD_SHOWING__ = false;
      }
    }
    // Remove loadAd and cleanup from dependencies to prevent re-running
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-center items-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.95)',
        pointerEvents: 'auto',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : 'clamp(1rem, 8vh, 120px)',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '1rem'
      }}
    >
      <div className="relative w-full max-w-3xl mx-4 flex flex-col items-center">
        {/* Ad container */}
        <div
          ref={adDisplayContainerRef}
          className="w-full bg-black relative rounded-lg overflow-hidden"
          style={{
            aspectRatio: '16/9',
            minHeight: '360px',
            maxHeight: '70vh',
            maxWidth: '640px', // Ensure the container doesn't exceed 640px width for 16:9
          }}
        >
          {/* Video element for IMA SDK */}
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            playsInline
          />
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
            <div className="text-white text-lg">Loading ad...</div>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-90">
            <div className="text-white text-lg mb-4">Ad unavailable</div>
            <button
              onClick={onComplete}
              className="px-6 py-3 rounded-lg font-semibold transition-all"
              style={{
                background: '#fffaeb',
                color: '#3e2615',
                boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
                border: '2px solid #8b6542',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Continue to Game
            </button>
          </div>
        )}

        {/* Skip button (only show after ad starts, if skippable) */}
        {isPlaying && (
          <div className="absolute bottom-4 right-4">
            <div className="text-white text-sm opacity-75">
              Ad playing...
            </div>
          </div>
        )}
        
        {/* Cancel button - always visible for mobile */}
        {isMobile && (
          <button
            onClick={onComplete}
            className="mt-4 px-6 py-2 rounded-lg font-semibold transition-all text-sm"
            style={{
              background: '#fffaeb',
              color: '#3e2615',
              boxShadow: '0 2px 0 #8b6542, 0 4px 8px rgba(0,0,0,0.2)',
              border: '2px solid #8b6542',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Skip Ad
          </button>
        )}
      </div>
    </div>
  );
}

